/**
 * NoHouseholdState — the shared "signed-in parent with zero households"
 * empty state, rendered by /my-family and the /billing family-balance
 * view (and any future parent surface that requires a household).
 *
 * A parent can be fully authenticated with no household for several
 * legitimate reasons (self-signup with no school claim yet, an invite
 * whose claim step no-op'd), so the copy never blames a missed email —
 * the actual unblock is adding a student, which creates the household.
 *
 * The component owns the whole flow: the `EmptyState` presentation AND
 * the `AddMemberSheet` opened straight to the Add Student chooser
 * (school code vs manual) — the same sheet /my-family uses, not a fork.
 * Callers only pass `onAdded` to refetch their own query once a student
 * lands.
 *
 * The secondary "contact your school" line covers the silent
 * claim-code mismatch path: an unmatched activation code routes
 * to a staff review queue with no user-facing signal, so a parent who
 * already tried needs a human escape hatch, not a retry loop.
 */

import { Button } from '@ordo/ui/button'
import { EmptyState } from '@ordo/ui/empty-state'
import type { SearchableSelectOption } from '@ordo/ui/searchable-select'
import { Plus, Users } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useTagsByKindQuery } from '@/api/family'

import { AddMemberSheet } from './add-member-sheet'

export function NoHouseholdState({ onAdded }: { onAdded?: () => void }) {
  const [addOpen, setAddOpen] = useState(false)

  // Allergen + dietary catalogs feed the add sheet's multi-selects.
  // TanStack Query dedupes these against /my-family's copies by key.
  const allergenQuery = useTagsByKindQuery('ALLERGEN')
  const dietaryQuery = useTagsByKindQuery('DIETARY')

  const allergenOptions = useMemo<SearchableSelectOption[]>(
    () => (allergenQuery.data?.tags ?? []).map((t) => ({ value: t.id, label: t.name })),
    [allergenQuery.data],
  )
  const dietaryOptions = useMemo<SearchableSelectOption[]>(
    () => (dietaryQuery.data?.tags ?? []).map((t) => ({ value: t.id, label: t.name })),
    [dietaryQuery.data],
  )

  return (
    <>
      <EmptyState
        icon={<Users />}
        title="Add a student to get started"
        description={
          <>
            Add your first student to set up your household and start ordering meals.
            <span className="mt-2 block text-xs">
              Having trouble linking a student? Contact your school and they&apos;ll get you connected.
            </span>
          </>
        }
        action={
          <Button variant="default" onClick={() => setAddOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden />
            Add student
          </Button>
        }
      />

      <AddMemberSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        initialMode="student-method"
        allergenOptions={allergenOptions}
        dietaryOptions={dietaryOptions}
        onAdded={onAdded}
      />
    </>
  )
}
